import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'
import { Navigate, Route, Routes, useParams, useLocation, matchPath } from 'react-router-dom'

import * as models from '@owl-nest/models'

import * as plume from '@ulule/owl-kit-components/next'

import { useHashRouting, useNavigation } from './hooks/useNavigation'
import * as projectModel from './models/project'
import { PATHS } from './paths'
import { RootState } from '../reducers'

import { BackersTab } from './views/detail/tabs/backers'
import { NewsSkeleton } from './views/detail/components/skeleton/NewsSkeleton'
import { NewsPermalinkSkeleton } from './views/detail/components/skeleton/NewsPermalinkSkeleton'

import * as UFE from '../UFE'

const CommentsTab = React.lazy(async () => ({ default: (await import('./views/detail/tabs/comments')).CommentsTab }))
const FAQTab = React.lazy(async () => ({ default: (await import('./views/detail/tabs/faq')).FAQTab }))
const NewsTab = React.lazy(async () => ({ default: (await import('./views/detail/tabs/news')).NewsTab }))
const ProjectTab = React.lazy(async () => ({ default: (await import('./views/detail/tabs/project')).ProjectTab }))
const RewardsTab = React.lazy(async () => ({ default: (await import('./views/detail/tabs/rewards')).RewardsTab }))
const NewsPermalink = React.lazy(async () => ({
  default: (await import('./views/detail/tabs/news/NewsPermalink')).NewsPermalink,
}))

export function TabRouter(): React.ReactElement<any> {
  useHashRouting()
  const navigation = useNavigation()
  const location = useLocation()

  const isNews = !!matchPath(PATHS.NEWS, location.pathname)
  const isNewsDetail = !!matchPath(`${PATHS.NEWS}:slug/`, location.pathname)

  return (
    <React.Suspense
      fallback={
        <>
          {isNews ? (
            <NewsSkeleton />
          ) : isNewsDetail ? (
            <NewsPermalinkSkeleton />
          ) : (
            <SpinnerContainer style={{ minHeight: `${'800'}px`, display: 'block', margin: 'auto', maxWidth: '300px' }}>
              <plume.Spinner />
            </SpinnerContainer>
          )}{' '}
        </>
      }
    >
      <Routes>
        <Route path={navigation.pathTo(PATHS.PROJECT).pathname} element={<HashRouter />} />
        <Route
          path={navigation.pathTo({ path: PATHS.NEWS_PERMALINK, params: { slug: ':slug' } }).pathname}
          element={<NewPermalinkView />}
        />
        <Route path={navigation.pathTo(PATHS.NEWS).pathname} element={<NewsTab />} />
        <Route path={navigation.pathTo(PATHS.SUPPORTERS).pathname} element={<BackersTab />} />

        <Route element={<Navigate to={navigation.pathTo(PATHS.PROJECT).pathname} />} />
      </Routes>
    </React.Suspense>
  )
}

const SpinnerContainer = styled.div`
  min-height: 800px;
  margin: auto;
  max-width: 300px;
`

function HashRouter(): React.ReactElement<any> {
  const location = useLocation()
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)

  if (location.hash.includes('#comments')) {
    return <CommentsTab />
  } else if (location.hash.includes('#faq')) {
    return <FAQTab />
    // HACK: The check for donation-based campaigns is quick'n'dirty.
    //  We should straightaway not register the excluded routes.
  } else if (location.hash.includes('#rewards') && !models.project.isDonationBased(project)) {
    return <RewardsTab />
  }

  return <ProjectTab />
}

function NewPermalinkView(): React.ReactElement<any> {
  const { slug } = useParams()
  const fragments = slug!.split('-')
  const id = Number(fragments[fragments.length - 1])
  // TODO: investigate why UFE.NEWS is not correctly typed
  return <NewsPermalink news={UFE.NEWS as any} id={id} />
}
